import diff from '@jest/diff-sequences';

import {
    type Block,
    Data,
    type Document,
    type Fragment,
    type Inline,
    type Text,
} from '@gitbook/slate';
import { List, OrderedMap } from '@gitbook/util-immutable';
import { compareOrderIndexes, createOrderIndexesBetween } from '@gitbook/util-orderindex';
import { assertNever } from '@gitbook/util-ts';

import * as table from '../table';
import {
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_MATH,
    BLOCK_TABLE,
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    type BlockTableData,
    INLINE_IMAGE,
    INLINE_MATH,
    LEAF_TEXT_BLOCKS_SET,
    type TableDefinitions,
    type TableRecords,
    type TableView,
    VOID_BLOCKS,
} from '../types';
import { compareStrings } from '../utils';
import { isDataEqual } from './utils';

const DATA_SENSITIVE_VOID_TYPES = new Set<string>([
    ...VOID_BLOCKS,
    BLOCK_IMAGE,
    BLOCK_MATH,
    INLINE_IMAGE,
    INLINE_MATH,
]);

/**
 * When 2 documents are semantically the same (or with slight differences) but different keys,
 * this function will try to reuse the keys of the first document with the content of the second one.
 */
export function reuseDocumentKeys(original: Document, updated: Document): Document {
    return updated.merge({
        key: original.key,
        nodes: reuseKeys(original.nodes, updated.nodes),
    });
}

/**
 * When 2 blocks are semantically the same (or with slight differences) but different keys,
 * this function will try to reuse the keys of the first block with the content of the second one.
 */
export function reuseBlockKeys(original: Block, updated: Block): Block {
    if (updated.type === BLOCK_TABLE && original.type === BLOCK_TABLE) {
        return reuseTableBlocks(original, updated);
    }

    return updated.merge({
        key: original.key,
        nodes: reuseKeys<Block | Inline | Text>(original.nodes, updated.nodes),
        fragments: reuseFragments(original.fragments, updated.fragments),
    });
}

function reuseKeys<NodeType extends Block | Inline | Text | Fragment>(
    original: List<NodeType>,
    updated: List<NodeType>
): List<NodeType> {
    const updatedToOriginal = new Map<string, string>();
    const usedOriginalKeys = new Set<string>();

    // Resolve matched nodes in constant time instead of scanning the original siblings for every match.
    const originalByKey = new Map<string, NodeType>();

    original.forEach((originalNode) => {
        if (!originalByKey.has(originalNode.key)) {
            originalByKey.set(originalNode.key, originalNode);
        }
    });

    updated.forEach((updatedNode, index) => {
        const originalNode = original.get(index);

        if (
            originalNode &&
            originalNode.object === updatedNode.object &&
            originalNode.key === updatedNode.key
        ) {
            updatedToOriginal.set(updatedNode.key, originalNode.key);
            usedOriginalKeys.add(originalNode.key);
        }
    });

    // Must run before the fuzzy diff below: that one only asks whether two nodes are similar
    // enough, so it cannot prefer an exact table over a nearly identical one.
    reuseExactTableKeys(original, updated, updatedToOriginal, usedOriginalKeys);

    diff(
        original.size,
        updated.size,
        (originalIndex, updatedIndex) => {
            const originalNode = original.get(originalIndex);
            const updatedNode = updated.get(updatedIndex);

            if (originalNode && updatedNode && originalNode.object === updatedNode.object) {
                const score = compareNodes(originalNode, updatedNode);
                if (score < 0.5) {
                    return true;
                }
            }

            return false;
        },
        (nCommon, originalIndex, updatedIndex) => {
            for (let i = 0; i < nCommon; i++) {
                const originalNode = original.get(originalIndex + i);
                const updatedNode = updated.get(updatedIndex + i);

                if (originalNode && updatedNode) {
                    if (
                        !updatedToOriginal.has(updatedNode.key) &&
                        !usedOriginalKeys.has(originalNode.key)
                    ) {
                        updatedToOriginal.set(updatedNode.key, originalNode.key);
                        usedOriginalKeys.add(originalNode.key);
                    }
                }
            }
        }
    );

    updated.forEach((updatedNode, index) => {
        const originalNode = original.get(index);

        if (
            originalNode &&
            !updatedToOriginal.has(updatedNode.key) &&
            !usedOriginalKeys.has(originalNode.key) &&
            shouldAlwaysReuseKeyByPosition(originalNode, updatedNode)
        ) {
            updatedToOriginal.set(updatedNode.key, originalNode.key);
            usedOriginalKeys.add(originalNode.key);
        }
    });

    if (original.size === updated.size) {
        updated.forEach((updatedNode, index) => {
            const originalNode = original.get(index);

            if (
                originalNode &&
                !updatedToOriginal.has(updatedNode.key) &&
                !usedOriginalKeys.has(originalNode.key) &&
                canReuseKeyByPosition(originalNode, updatedNode)
            ) {
                updatedToOriginal.set(updatedNode.key, originalNode.key);
                usedOriginalKeys.add(originalNode.key);
            }
        });
    }

    updated.forEach((updatedNode) => {
        if (updatedToOriginal.has(updatedNode.key)) {
            return;
        }

        const originalNode = findReusableMovedVoidNode(
            original,
            updated,
            updatedNode,
            updatedToOriginal,
            usedOriginalKeys
        );

        if (originalNode) {
            updatedToOriginal.set(updatedNode.key, originalNode.key);
            usedOriginalKeys.add(originalNode.key);
        }
    });

    return updated.map((node): NodeType => {
        const matchingOriginalKey = updatedToOriginal.get(node.key);
        const matchingOriginalNode = matchingOriginalKey
            ? originalByKey.get(matchingOriginalKey)
            : null;

        if (!matchingOriginalNode) {
            return node;
        }

        if (node.object === 'text' && matchingOriginalNode.object === 'text') {
            return node.merge({
                key: matchingOriginalNode.key,
            }) as NodeType;
        }

        if (node.object === 'inline' && matchingOriginalNode.object === 'inline') {
            return node.merge({
                key: matchingOriginalNode.key,
                nodes: reuseKeys(matchingOriginalNode.nodes, node.nodes),
                fragments: reuseFragments(matchingOriginalNode.fragments, node.fragments),
            }) as NodeType;
        }

        if (node.object === 'block' && matchingOriginalNode.object === 'block') {
            return reuseBlockKeys(matchingOriginalNode, node) as NodeType;
        }

        if (node.object === 'fragment' && matchingOriginalNode.object === 'fragment') {
            return reuseFragmentKeys(matchingOriginalNode, node) as NodeType;
        }

        throw new Error(
            `Mismatching node types: ${node.object} !== ${matchingOriginalNode.object}`
        );
    });
}

/**
 * Return whether a same-position node should reuse the original key even when
 * surrounding sibling counts differ. Updates and image galleries often keep the
 * same logical position while their nested entries get re-keyed or reordered;
 * keeping their positional keys prevents local edits from rendering as
 * delete/add around following blocks.
 */
function shouldAlwaysReuseKeyByPosition<NodeType extends Block | Inline | Text | Fragment>(
    original: NodeType,
    updated: NodeType
): boolean {
    if (original.object !== 'block' || updated.object !== 'block') {
        return false;
    }

    if (original.type !== updated.type) {
        return false;
    }

    return (
        original.type === BLOCK_UPDATES ||
        original.type === BLOCK_UPDATE ||
        original.type === BLOCK_IMAGES
    );
}

function canReuseKeyByPosition<NodeType extends Block | Inline | Text | Fragment>(
    original: NodeType,
    updated: NodeType
): boolean {
    if (original.object !== updated.object) {
        return false;
    }

    if (original.object === 'text') {
        return true;
    }

    if (original.object === 'fragment') {
        return false;
    }

    if (isVoidNode(original) || isVoidNode(updated)) {
        if (
            (original.object !== 'block' && original.object !== 'inline') ||
            (updated.object !== 'block' && updated.object !== 'inline')
        ) {
            return false;
        }

        return (
            original.isVoid &&
            updated.isVoid &&
            original.type === updated.type &&
            isDataEqual(original.data, updated.data)
        );
    }

    if (
        (original.object === 'block' || original.object === 'inline') &&
        (updated.object === 'block' || updated.object === 'inline') &&
        original.type === updated.type
    ) {
        return true;
    }

    return isLeafTextBlock(original) && isLeafTextBlock(updated);
}

function findReusableMovedVoidNode<NodeType extends Block | Inline | Text | Fragment>(
    original: List<NodeType>,
    updated: List<NodeType>,
    updatedNode: NodeType,
    updatedToOriginal: Map<string, string>,
    usedOriginalKeys: Set<string>
): NodeType | null {
    const originalCandidates = original.filter(
        (originalNode) =>
            !usedOriginalKeys.has(originalNode.key) &&
            canReuseMovedVoidKey(originalNode, updatedNode)
    );

    if (originalCandidates.size !== 1) {
        return null;
    }

    const originalCandidate = originalCandidates.first();
    if (!originalCandidate) {
        return null;
    }

    const updatedCandidates = updated.filter(
        (otherUpdatedNode) =>
            !updatedToOriginal.has(otherUpdatedNode.key) &&
            canReuseMovedVoidKey(originalCandidate, otherUpdatedNode)
    );

    return updatedCandidates.size === 1 ? originalCandidate : null;
}

function canReuseMovedVoidKey<NodeType extends Block | Inline | Text | Fragment>(
    original: NodeType,
    updated: NodeType
): boolean {
    if (
        (original.object !== 'block' && original.object !== 'inline') ||
        (updated.object !== 'block' && updated.object !== 'inline')
    ) {
        return false;
    }

    if (!isVoidNode(original) || !isVoidNode(updated) || original.type !== updated.type) {
        return false;
    }

    if (!isDataEqual(original.data, updated.data)) {
        return false;
    }

    return serializeDataForComparison(original.data.toFrozenObject()) !== '';
}

function reuseFragments(
    original: OrderedMap<string, Fragment>,
    updated: OrderedMap<string, Fragment>
): OrderedMap<string, Fragment> {
    const reused = reuseKeys(
        List.create(original.toValuesArray()),
        List.create(updated.toValuesArray())
    );
    return OrderedMap.create(
        reused.toArray().map((fragment): [string, Fragment] => [fragment.fragmentKey, fragment])
    );
}

function reuseFragmentKeys(original: Fragment, updated: Fragment): Fragment {
    return updated.merge({
        key: original.key,
        // @ts-expect-error - Problem of typing in slate
        fragment: original.fragment,
        nodes: reuseKeys(original.nodes, updated.nodes),
    });
}

function reuseTableBlocks(original: Block, updated: Block): Block {
    const originalTableData = original.data.toFrozenObject() as BlockTableData;
    const updatedTableData = updated.data.toFrozenObject() as BlockTableData;

    const { definition, updatedToOriginal: definitionUpdatedToOriginal } = reuseTableDefinitions(
        originalTableData,
        updatedTableData
    );

    const {
        records,
        updatedFragmentsToOriginal,
        updatedToOriginal: recordUpdatedToOriginal,
    } = reuseTableRecords(
        { block: original, data: originalTableData },
        { block: updated, data: updatedTableData },
        definitionUpdatedToOriginal
    );

    const newTableData: BlockTableData = {
        ...updatedTableData,
        definition,
        records,
        view: mapTableViewToUpdatedDefinition(updatedTableData, definitionUpdatedToOriginal),
        ...(updatedTableData.cellMerges
            ? {
                  cellMerges: updatedTableData.cellMerges.map((merge) => ({
                      ...merge,
                      anchor: {
                          record:
                              recordUpdatedToOriginal.get(merge.anchor.record) ??
                              merge.anchor.record,
                          column: getUpdatedTableColumnKey(
                              merge.anchor.column,
                              definitionUpdatedToOriginal
                          ),
                      },
                  })),
              }
            : {}),
    };

    return updated.merge({
        key: original.key,
        // @ts-expect-error
        data: Data.create(newTableData),
        // We re-use the text node in the void block
        nodes: original.nodes,
        // We re-use the fragments from the original table when possible
        fragments: OrderedMap.create(
            updated.fragments.toArray().map(([key, fragment]): [string, Fragment] => {
                const originalFragmentKey = updatedFragmentsToOriginal.get(key);
                if (!originalFragmentKey) {
                    return [key, fragment];
                }

                const originalFragment = original.fragments.get(originalFragmentKey);
                if (!originalFragment) {
                    return [key, fragment];
                }

                const newFragment = reuseFragmentKeys(originalFragment, fragment);
                return [newFragment.fragmentKey, newFragment];
            })
        ),
    });
}

/**
 * Reuse the IDs of the original table definitions with the updated table definitions.
 */
function reuseTableDefinitions(
    original: BlockTableData,
    updated: BlockTableData
): { definition: TableDefinitions; updatedToOriginal: Map<string, string> } {
    const updatedToOriginal = new Map<string, string>();

    const allOriginalColumnKeys = getAllTableColumnKeys(original);
    const allUpdatedColumnKeys = getAllTableColumnKeys(updated);

    diff(
        allOriginalColumnKeys.length,
        allUpdatedColumnKeys.length,
        (originalIndex, updatedIndex) => {
            const originalColumn = original.definition[allOriginalColumnKeys[originalIndex]];
            const updatedColumn = updated.definition[allUpdatedColumnKeys[updatedIndex]];

            if (originalColumn && updatedColumn && originalColumn.type === updatedColumn.type) {
                const score = compareStrings(originalColumn.title, updatedColumn.title);
                if (score < 0.5) {
                    return true;
                }
            }

            return false;
        },
        (nCommon, originalIndex, updatedIndex) => {
            for (let i = 0; i < nCommon; i++) {
                const originalKey = allOriginalColumnKeys[originalIndex + i];
                const updatedKey = allUpdatedColumnKeys[updatedIndex + i];

                if (originalKey && updatedKey) {
                    updatedToOriginal.set(updatedKey, originalKey);
                }
            }
        }
    );

    return {
        definition: Object.fromEntries(
            Object.entries(updated.definition).map(([key, value]) => {
                const updatedKey = getUpdatedTableColumnKey(key, updatedToOriginal);
                return [
                    updatedKey,
                    {
                        ...value,
                        id: updatedKey,
                    },
                ];
            })
        ),
        updatedToOriginal,
    };
}

/**
 * Reuse the IDs of the original table records with the updated table records.
 */
function reuseTableRecords(
    original: { block: Block; data: BlockTableData },
    updated: { block: Block; data: BlockTableData },
    definitionUpdatedToOriginal: Map<string, string>
): {
    records: TableRecords;
    updatedFragmentsToOriginal: Map<string, string>;
    updatedToOriginal: Map<string, string>;
} {
    const originalRecordKeys = table.defaultRecordSortedKeys(original.data);
    const updatedRecordKeys = table.defaultRecordSortedKeys(updated.data);

    // The diff probes the same row pairs repeatedly, so build their comparison text only once.
    const originalRecordTexts = originalRecordKeys.map((recordKey) => {
        const record = original.data.records[recordKey];
        return record ? table.getTableRowPlainTexts(original.block, record).join(' ') : null;
    });
    const updatedRecordTexts = updatedRecordKeys.map((recordKey) => {
        const record = updated.data.records[recordKey];
        return record ? table.getTableRowPlainTexts(updated.block, record).join(' ') : null;
    });

    const updatedToOriginal = new Map<string, string>();
    const updatedFragmentsToOriginal = new Map<string, string>();

    // Re-serialization commonly changes IDs without changing row order. Mapping exact rows
    // directly avoids the expensive fuzzy diff and its ambiguity around similar rows.
    if (
        originalRecordTexts.length === updatedRecordTexts.length &&
        originalRecordTexts.every(
            (originalText, index) =>
                originalText !== null && originalText === updatedRecordTexts[index]
        )
    ) {
        updatedRecordKeys.forEach((updatedKey, index) => {
            const originalKey = originalRecordKeys[index];
            if (originalKey) {
                updatedToOriginal.set(updatedKey, originalKey);
            }
        });
    } else {
        diff(
            originalRecordKeys.length,
            updatedRecordKeys.length,
            (originalIndex, updatedIndex) => {
                const originalRecordKey = originalRecordKeys[originalIndex];
                const updatedRecordKey = updatedRecordKeys[updatedIndex];

                if (!originalRecordKey || !updatedRecordKey) {
                    return false;
                }

                const originalRecordText = originalRecordTexts[originalIndex];
                const updatedRecordText = updatedRecordTexts[updatedIndex];

                if (originalRecordText === null || updatedRecordText === null) {
                    return false;
                }

                return (
                    originalRecordText === updatedRecordText ||
                    compareStrings(originalRecordText, updatedRecordText) < 0.5
                );
            },
            (nCommon, originalIndex, updatedIndex) => {
                for (let i = 0; i < nCommon; i++) {
                    const originalKey = originalRecordKeys[originalIndex + i];
                    const updatedKey = updatedRecordKeys[updatedIndex + i];

                    if (originalKey && updatedKey) {
                        updatedToOriginal.set(updatedKey, originalKey);
                    }
                }
            }
        );
    }

    const orderIndexes = reuseRecordOrderIndexes(
        original.data,
        updatedRecordKeys,
        updatedToOriginal
    );

    return {
        records: Object.fromEntries(
            Object.entries(updated.data.records).map(([key, record]) => {
                const updatedRecordKey = updatedToOriginal.get(key) ?? key;

                // We update all the values of the record to the new definition keys
                const mappedValues = Object.fromEntries(
                    Object.entries(record.values).map(([columnKey, cellValue]) => {
                        let newCellValue = cellValue;
                        const updatedColumnDefinition = updated.data.definition[columnKey];
                        const newDefinitionKey = getUpdatedTableColumnKey(
                            columnKey,
                            definitionUpdatedToOriginal
                        );

                        // If the column is a text, we map the updated fragment key to the original fragment key
                        if (
                            updatedColumnDefinition.type === 'text' &&
                            typeof cellValue === 'string'
                        ) {
                            const originalFragmentKey =
                                original.data.records[updatedRecordKey]?.values[newDefinitionKey];

                            if (
                                typeof originalFragmentKey === 'string' &&
                                // Only map to original fragments if they do actually exist
                                original.block.fragments.has(originalFragmentKey)
                            ) {
                                newCellValue = originalFragmentKey;
                                updatedFragmentsToOriginal.set(cellValue, originalFragmentKey);
                            }
                        }

                        return [newDefinitionKey, newCellValue];
                    })
                );

                return [
                    updatedRecordKey,
                    {
                        ...record,
                        orderIndex: orderIndexes.get(key) ?? record.orderIndex,
                        values: mappedValues,
                    },
                ];
            })
        ),
        updatedFragmentsToOriginal,
        updatedToOriginal,
    };
}

/**
 * Give matched records the order index of their original record, and unmatched records one between
 * their neighbours. Parsing assigns fresh order indexes, so without this an unchanged table differs
 * from the original and conflicts when both sides of a merge were re-parsed.
 *
 * Returns no overrides when the original indexes would not keep the updated row order.
 */
function reuseRecordOrderIndexes(
    original: BlockTableData,
    /** Updated record keys, in display order. */
    updatedRecordKeys: string[],
    updatedToOriginal: Map<string, string>
): Map<string, string> {
    const reused = updatedRecordKeys.map((key) => {
        const originalKey = updatedToOriginal.get(key);
        return originalKey ? original.records[originalKey]?.orderIndex : undefined;
    });

    if (reused.every((orderIndex) => orderIndex === undefined)) {
        return new Map();
    }

    const orderIndexes = new Map<string, string>();
    let previous: string | undefined;
    let index = 0;
    while (index < updatedRecordKeys.length) {
        const orderIndex = reused[index];
        if (orderIndex !== undefined) {
            orderIndexes.set(updatedRecordKeys[index]!, orderIndex);
            previous = orderIndex;
            index += 1;
            continue;
        }

        // Lay out each run of unmatched records at once, so their indexes stay short.
        let end = index;
        while (end < reused.length && reused[end] === undefined) {
            end += 1;
        }
        createOrderIndexesBetween(end - index, { before: previous, after: reused[end] }).forEach(
            (runIndex, offset) => orderIndexes.set(updatedRecordKeys[index + offset]!, runIndex)
        );
        index = end;
    }

    // Original indexes can be out of order, or not ranks at all (then new ones can't be placed
    // between them), so check the result keeps the updated row order.
    const isOrdered = updatedRecordKeys.every(
        (key, position) =>
            position === 0 ||
            compareOrderIndexes(
                { orderIndex: orderIndexes.get(updatedRecordKeys[position - 1]!)! },
                { orderIndex: orderIndexes.get(key)! }
            ) < 0
    );

    return isOrdered ? orderIndexes : new Map();
}

/**
 * Reuse the ID in the view of the original table with the updated table.
 */
function mapTableViewToUpdatedDefinition(
    updated: BlockTableData,
    definitionUpdatedToOriginal: Map<string, string>
): TableView {
    switch (updated.view.type) {
        case 'grid':
            return {
                ...updated.view,
                columns: updated.view.columns.map((column) =>
                    getUpdatedTableColumnKey(column, definitionUpdatedToOriginal)
                ),
                ...(updated.view.columnWidths
                    ? {
                          columnWidths: Object.fromEntries(
                              Object.entries(updated.view.columnWidths).map(([key, value]) => [
                                  getUpdatedTableColumnKey(key, definitionUpdatedToOriginal),
                                  value,
                              ])
                          ),
                      }
                    : {}),
            };
        case 'cards':
            return {
                ...updated.view,
                columns: updated.view.columns.map((column) =>
                    getUpdatedTableColumnKey(column, definitionUpdatedToOriginal)
                ),
                ...(updated.view.coverDefinition
                    ? {
                          coverDefinition: getUpdatedTableColumnKey(
                              updated.view.coverDefinition,
                              definitionUpdatedToOriginal
                          ),
                      }
                    : {}),
                ...(updated.view.coverDefinitionDark
                    ? {
                          coverDefinitionDark: getUpdatedTableColumnKey(
                              updated.view.coverDefinitionDark,
                              definitionUpdatedToOriginal
                          ),
                      }
                    : {}),
                ...(updated.view.targetDefinition
                    ? {
                          targetDefinition: getUpdatedTableColumnKey(
                              updated.view.targetDefinition,
                              definitionUpdatedToOriginal
                          ),
                      }
                    : {}),
            };
        default:
            assertNever(updated.view);
    }
}

/**
 * List all the column keys in a table, sorting them by the display order.
 */
function getAllTableColumnKeys(tableData: BlockTableData): string[] {
    return Object.keys(tableData.definition).sort((a, b) => {
        return tableData.view.columns.indexOf(a) - tableData.view.columns.indexOf(b);
    });
}

/**
 * Return the key to use for a column definition.
 */
function getUpdatedTableColumnKey(
    updatedKey: string,
    definitionUpdatedToOriginal: Map<string, string>
): string {
    const originalKey = definitionUpdatedToOriginal.get(updatedKey);
    if (!originalKey) {
        return updatedKey;
    }

    return originalKey;
}

/**
 * Compare two nodes and return a score between 0 and 1.
 * 0: nodes are identical
 * 1: nodes are different
 */
function compareNodes<NodeType extends Block | Inline | Text | Fragment>(
    original: NodeType,
    updated: NodeType
): number {
    // Leaf text blocks are the only one who can have their type mutate
    if (
        original.type !== updated.type &&
        (!isLeafTextBlock(original) || !isLeafTextBlock(updated))
    ) {
        return 1;
    }

    if (isVoidNode(original) || isVoidNode(updated)) {
        if (
            (original.object !== 'block' && original.object !== 'inline') ||
            (updated.object !== 'block' && updated.object !== 'inline') ||
            !original.isVoid ||
            !updated.isVoid ||
            original.type !== updated.type
        ) {
            return 1;
        }

        return compareVoidNodeData(original, updated);
    }

    return compareStrings(original.getCompleteText(), updated.getCompleteText());
}

function isVoidNode(node: Block | Inline | Text | Fragment): boolean {
    return (
        (node.object === 'block' || node.object === 'inline') &&
        (node.isVoid || DATA_SENSITIVE_VOID_TYPES.has(node.type))
    );
}

function compareVoidNodeData(original: Block | Inline, updated: Block | Inline): number {
    if (isDataEqual(original.data, updated.data)) {
        return 0;
    }

    if (
        original.object === 'block' &&
        updated.object === 'block' &&
        original.type === BLOCK_TABLE
    ) {
        const contentScore = compareTableContent(original, updated);
        if (contentScore !== null) {
            return contentScore;
        }

        const originalData = serializeDataForComparison(original.data.toFrozenObject());
        const updatedData = serializeDataForComparison(updated.data.toFrozenObject());

        if (!originalData || !updatedData) {
            return 1;
        }

        return compareStrings(originalData, updatedData);
    }

    return 1;
}

/**
 * Compare two tables on their content, returning a score between 0 and 1 like `compareNodes`.
 * Returns null when the tables cannot be compared this way and the caller should fall back.
 *
 * Comparing the serialized data instead would be dominated by the record and column IDs, which
 * are regenerated on every re-serialization: two identical large tables score as different.
 */
function compareTableContent(original: Block, updated: Block): number | null {
    const originalLines = getTableContentLines(original);
    const updatedLines = getTableContentLines(updated);

    if (!originalLines || !updatedLines) {
        return null;
    }

    const totalLines = originalLines.length + updatedLines.length;
    if (totalLines === 0) {
        return 0;
    }

    let commonLines = 0;

    diff(
        originalLines.length,
        updatedLines.length,
        (originalIndex, updatedIndex) =>
            originalLines[originalIndex] === updatedLines[updatedIndex],
        (nCommon) => {
            commonLines += nCommon;
        }
    );

    // Ratio of the lines the two tables do not share: 0 when every line is common, 1 when none is.
    // Counting the common lines on both sides is what makes an added or removed row weigh the same
    // as an edited one.
    return 1 - (2 * commonLines) / totalLines;
}

/**
 * Flatten a table into comparable lines: one per column definition, then one per row.
 * Returns null for tables holding a column whose values their plain text does not capture.
 */
function getTableContentLines(block: Block): string[] | null {
    const data = block.data.toFrozenObject() as BlockTableData;
    const columnKeys = getAllTableColumnKeys(data);
    const lines: string[] = [];

    for (const columnKey of columnKeys) {
        const definition = data.definition[columnKey];

        // Every other column type renders as an empty string below, which would make two tables
        // holding entirely different values look identical. Leave those to the caller's fallback.
        if (definition?.type !== 'text' && definition?.type !== 'number') {
            return null;
        }

        // Prefixed so that a column title can never be mistaken for a row with the same text.
        lines.push(`column:${definition.type}:${definition.title}`);
    }

    for (const recordKey of table.defaultRecordSortedKeys(data)) {
        const record = data.records[recordKey];

        if (!record) {
            return null;
        }

        lines.push(`row:${table.getTableRowPlainTexts(block, record).join(' ')}`);
    }

    return lines;
}

/**
 * Map the tables whose content is identical, before the fuzzy diff runs. A table differing by a
 * row or two still scores as a match, so without this anchor it could claim the key of the table
 * that actually matches. Only unambiguous matches are claimed: when the same content appears
 * twice on either side, the fuzzy diff remains responsible for pairing them.
 */
function reuseExactTableKeys<NodeType extends Block | Inline | Text | Fragment>(
    original: List<NodeType>,
    updated: List<NodeType>,
    updatedToOriginal: Map<string, string>,
    usedOriginalKeys: Set<string>
): void {
    const originalKeysByContent = getTableKeysByContent(
        original,
        (node) => !usedOriginalKeys.has(node.key)
    );

    if (originalKeysByContent.size === 0) {
        return;
    }

    const updatedKeysByContent = getTableKeysByContent(
        updated,
        (node) => !updatedToOriginal.has(node.key)
    );

    updatedKeysByContent.forEach((updatedKey, content) => {
        const originalKey = originalKeysByContent.get(content);

        // A null key on either side marks content that appears more than once, and therefore
        // has no single table to pair with.
        if (!updatedKey || !originalKey || usedOriginalKeys.has(originalKey)) {
            return;
        }

        updatedToOriginal.set(updatedKey, originalKey);
        usedOriginalKeys.add(originalKey);
    });
}

/**
 * Index the available table blocks by their content, mapping to null when the same content
 * appears more than once and picking one of them would be arbitrary.
 */
function getTableKeysByContent<NodeType extends Block | Inline | Text | Fragment>(
    nodes: List<NodeType>,
    isAvailable: (node: NodeType) => boolean
): Map<string, string | null> {
    const keysByContent = new Map<string, string | null>();

    nodes.forEach((node) => {
        if (node.object !== 'block' || node.type !== BLOCK_TABLE || !isAvailable(node)) {
            return;
        }

        const lines = getTableContentLines(node);
        if (!lines) {
            return;
        }

        const content = lines.join('\n');
        keysByContent.set(content, keysByContent.has(content) ? null : node.key);
    });

    return keysByContent;
}

function serializeDataForComparison(value: unknown): string {
    if (value === null || typeof value !== 'object') {
        return value === undefined ? '' : String(value);
    }

    const entries = Object.entries(value).filter(([, entryValue]) => entryValue !== undefined);
    if (entries.length === 0) {
        return '';
    }

    return JSON.stringify(Object.fromEntries(entries.sort(([a], [b]) => a.localeCompare(b))));
}

function isLeafTextBlock(node: Block | Inline | Text | Fragment): boolean {
    return node.object === 'block' && LEAF_TEXT_BLOCKS_SET.has(node.type);
}
